/**
 * Card - Glassmorphic card container component.
 *
 * Features:
 * - Dark background with blur effect
 * - Optional hover animation
 * - Customizable padding
 * - Optional header and footer sections
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  header?: ReactNode;
  footer?: ReactNode;
  hoverable?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  noBorder?: boolean;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      header,
      footer,
      hoverable = false,
      padding = 'md',
      noBorder = false,
      className = '',
      ...props
    },
    ref
  ) => {
    // Base classes
    const baseClasses = 'glass-panel shadow-card';

    // Border classes
    const borderClasses = noBorder ? '!border-0' : '';

    // Hover classes
    const hoverClasses = hoverable
      ? 'transition-all duration-300 hover:border-accent/30 hover:shadow-glow cursor-pointer'
      : '';

    // Padding classes for content
    const paddingClasses = {
      none: '',
      sm: 'p-3',
      md: 'p-5',
      lg: 'p-8',
    };

    return (
      <div
        ref={ref}
        className={`${baseClasses} ${borderClasses} ${hoverClasses} ${className}`}
        {...props}
      >
        {/* Header */}
        {header && (
          <div className="px-5 py-4 border-b border-white/5">{header}</div>
        )}

        {/* Content */}
        <div className={paddingClasses[padding]}>{children}</div>

        {/* Footer */}
        {footer && (
          <div className="px-5 py-4 border-t border-white/5">{footer}</div>
        )}
      </div>
    );
  }
);

Card.displayName = 'Card';

export default Card;
